import { useState } from 'react';
import { useDomains } from '../hooks/useDomains';
import { useSubscription } from '../contexts/SubscriptionContext';
import DomainCard from '../components/domains/DomainCard';
import AddDomainForm from '../components/domains/AddDomainForm';
import toast from 'react-hot-toast';
import { Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Domains = () => {
  const { domains, isLoading, error, addDomain, removeDomain, refreshDomain } = useDomains();
  const { certificateLimit } = useSubscription();
  const [filter, setFilter] = useState<'all' | 'valid' | 'warning' | 'expired'>('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const handleAddDomain = async (name: string) => {
    try {
      await addDomain(name);
    } catch {
      toast.error('Failed to add domain');
      throw new Error('Failed to add domain');
    }
  };

  const handleRemoveDomain = (id: string) => {
    try {
      removeDomain(id);
      toast.success('Domain removed successfully');
    } catch {
      toast.error('Failed to remove domain');
    }
  };

  const handleRefreshDomain = async (id: string) => {
    try {
      await refreshDomain(id);
      toast.success('Domain refreshed successfully');
    } catch {
      toast.error('Failed to refresh domain');
    }
  };

  const filteredDomains = domains.filter(domain => {
    if (filter !== 'all' && domain.status !== filter) return false;
    if (search && !domain.domain.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-danger-50 border border-danger-200 rounded-md p-4 text-danger-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Your Domains
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage all your domain SSL certificates.
          </p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border border-blue-200 rounded-lg bg-white px-3 py-1 shadow-sm" style={{ borderLeftWidth: 6, borderLeftColor: '#3b82f6' }}>
          <div className="flex-1 flex items-center gap-2 min-h-0">

            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyUp={e => setSearch((e.target as HTMLInputElement).value)}
              placeholder="Search domains..."
              className="ml-4 w-full md:w-64 px-2 py-1 border border-gray-200 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              style={{ minHeight: 0 }}
            />
          </div>
          <div className="flex-1 flex items-center justify-end min-h-0">
            <div className="w-full md:w-auto">
              <AddDomainForm
                onAddDomain={handleAddDomain}
                domainsCount={domains.length}
                domainsLimit={certificateLimit}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-medium text-gray-500">
            Showing {filteredDomains.length} of {domains.length} domains
          </div>
          <div className="mt-3 sm:mt-0">
            <div className="flex items-center space-x-2 rounded-md bg-white p-2 shadow-sm border border-gray-200">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-xs font-medium text-gray-500">Filter:</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | 'valid' | 'warning' | 'expired')}
                className="text-sm border-0 focus:outline-none focus:ring-0 text-gray-700 bg-transparent"
              >
                <option value="all">All</option>
                <option value="valid">Valid</option>
                <option value="warning">Warning</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {filteredDomains.length > 0 ? (
          <div className="mt-4 grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredDomains.map((domain) => (
              <div
                key={domain.domain}
                role="button"
                tabIndex={0}
                className="focus:outline-none"
                onClick={() => navigate(`/domains/${encodeURIComponent(domain.serial_number)}`)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/domains/${encodeURIComponent(domain.serial_number)}`); }}
                style={{ cursor: 'pointer' }}
              >
                <DomainCard
                  domain={domain}
                  onRefresh={handleRefreshDomain}
                  onRemove={handleRemoveDomain}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-4 bg-white shadow-sm overflow-hidden sm:rounded-md p-6 text-center">
            {domains.length > 0 ? (
              <p className="text-gray-500">No domains match the selected filter.</p>
            ) : (
              <>
                <p className="text-gray-500">You haven't added any domains yet.</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add your first domain using the form above.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Domains;